import { Component, EventEmitter, OnDestroy, OnInit, Input, Output, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription, map } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  selectPatientSearchFilter, selectPracticeLocation, selectPracticePatientStatus, selectPatientSearchStateEvent,
  selectBulkEmailPatientSearchFilter,
  selectBulkEmailPatientSearchStateEvent
} from '../../store/selectors/patient-search.selector';
import {
  bulkPatientSearchStateDestroy,
  bulkSelectedPatients,
  closeErrorAlert, invokedBulkPatientSearchFilter, invokedPatientSearchFilter, invokedPracticeLocationAPI,
} from '../../store/actions/patient-search.action';
import { AlertService } from 'src/app/features/shared/utils/alert.service';
import { CustomValidatorService } from 'src/app/features/shared/utils/custom-validator.service';
import { CommonUtilsService } from 'src/app/features/shared/utils/common-utils.service';
import { PatientBasicInfoModel, PatientSearchFilterModel, PatientSearchStateEvent, PracticeLocation, PracticePatientStatus } from '../../types/patient-search-type';
import { MASK_PATTERN } from 'src/app/features/shared/constant/shared-pattern-regex-asset';
import { DebounceService } from 'src/app/features/shared/services/debounce-service';
import { bulkPatientSearchFilterTypes, patientSearchMessage, patientSearchTypes } from '../../constant/patient-dashboard-api-asset';
import { MaskingType } from 'src/app/features/shared/constant/shared-constant';
import { DateRange } from 'src/app/features/shared/types/shared-types';
import { FormControlDateRangeComponent } from 'src/app/features/shared/components/form-controls/form-control-daterange/form-control-daterange.component';
import { featureName, moduleName, operationName } from 'src/app/features/user/constant/user-role-constant';
import { TreatmentType } from 'src/app/features/servicecontract/store/treatment/treatment-type/treatment-type.state';
import { TreatmentOption } from 'src/app/features/servicecontract/store/treatment/treatment-option/treatment-option.state';
import { selectTreatmentTypes } from 'src/app/features/servicecontract/store/treatment/treatment-type/treatment-type.selector';
import { selectPracticeTreatmentOptions } from 'src/app/features/servicecontract/store/treatment/treatment-option/treatment-option.selector';
import { AuthService } from 'src/app/features/user/services/authentication/auth.service';
import { invokeTreatmentTypes } from 'src/app/features/servicecontract/store/treatment/treatment-type/treatment-type.action';
import { invokePracticeTreatmentOptions } from 'src/app/features/servicecontract/store/treatment/treatment-option/treatment-option.action';

@Component({
  selector: 'app-patient-search-filter',
  templateUrl: './patient-search-filter.component.html',
})

export class PatientSearchFilterComponent implements OnInit, OnDestroy {
  @Input() allowToSelectPatient = false;
  @Input() isAutoSearch = true;
  @Input() pageIdentifier = patientSearchTypes.patientSearch;
  @Input() isReadOnlyFilter = false;
  @Input() startDate!: Date | null;
  @Input() endDate!: Date | null;

  @Output() submitPatients: EventEmitter<void> = new EventEmitter<void>();

  patientSearchFilter: FormGroup;
  isLoading: boolean | undefined;
  maskTeleUS = MASK_PATTERN.usMobile;
  maskDateSlash = MASK_PATTERN.dob;
  maskingType = MaskingType;
  bulkFilterTypes = bulkPatientSearchFilterTypes;
  patientSearchType = patientSearchTypes;
  private subscriptions$ = new Subscription();

  treatmentTypes: TreatmentType[] = [];
  practiceTreatmentOptions: TreatmentOption[] = [];

  practiceTreatmentTypes$ = this.store.select(selectTreatmentTypes);
  practiceTreatmentOptions$ = this.store.select(selectPracticeTreatmentOptions);

  bulkCommunicationCreateFeaturePermission = { moduleName: moduleName.patient, featureName: featureName.patient, operationName: operationName.create};

  patientSearchFilter$ = this.store.select(selectPatientSearchFilter);
  practicePatientStatus$ = this.store.select(selectPracticePatientStatus).pipe(map(this.sortPracticePtStatus));
  practiceLocation$ = this.store.select(selectPracticeLocation).pipe(map(this.sortPracticeLocation));
  isLoading$ = this.store.select(selectPatientSearchStateEvent);

  bulkEmailPatientSearchFilter$ = this.store.select(selectBulkEmailPatientSearchFilter);
  bulkStateEvent$ = this.store.select(selectBulkEmailPatientSearchStateEvent);
  
  @Output() closeSearchResultOverlay = new EventEmitter<boolean>();
  formSearchSubmitted = false;

  showAppointmentDateRange = false;
  showTreatmentType = false;
  showTreatmentOption = false;
  showPastdueDaysRange= false;
  showPatientStatus = false;
  selectAllLoading = false;
  practiceId = 0;
  selectedPatientsBasicInfo: PatientBasicInfoModel[] = [];

  constructor(private store: Store,
    private formBuilder: FormBuilder,
    private alert: AlertService,
    private commonUtilsService: CommonUtilsService,
    private debounceService: DebounceService,
    private authService: AuthService) {
      this.practiceId = this.authService.getPracticeIDFromToken();
      this.patientSearchFilter = this.formBuilder.group({
        firstName: [''],
        lastName: [''],
        patientName: [''],
        patientPreferredName: ['', [Validators.maxLength(100)]],
        patientDOB: ['', { asyncValidators: [CustomValidatorService.dobFromatValidator()] }],
        patientPrimaryPhone: [''],
        patientPrimaryEmailAddress: ['', [Validators.maxLength(100)]],
        patientFinancialResponsiblePersonFirstName: [''],
        patientFinancialResponsiblePersonLastName: [''],
        patientFinancialResponsiblePerson: ['', [Validators.maxLength(100)]],
        patientStatusId: [''],
        selectedPatientStatusId: [[]],
        practiceLocationId: [''],
        selectedPracticeLocations: [[]],
        SortOrder: [''],
        SortColumn: [''],
        tackAfter: [''],
        pageSize: [''],
        patientId: [''],
        showInActivePatients: [false],
        filter: [null],
        fromDate: [''],
        toDate: [''],
        fromDays: [''],
        toDays: [''],
        patientTreatmentTypes: [''],
        treatmentOption: [''],
        practiceTreatmentOptionIds: [[]],
        patientTreatmentTypeIds: [[]]
      });
  }

  ngOnInit(): void {
    this.store.dispatch(invokedPracticeLocationAPI({pageIdentifier: this.pageIdentifier}));
    this.store.dispatch(invokePracticeTreatmentOptions({ practiceId: this.practiceId }));
    this.store.dispatch(invokeTreatmentTypes({ practiceId: this.practiceId }));

    this.subscriptions$.add(this.practiceTreatmentTypes$.subscribe((treatmentTypes: TreatmentType[]) => { this.bindTreatmentTypes(treatmentTypes); }));
    this.subscriptions$.add(this.practiceTreatmentOptions$.subscribe((data: TreatmentOption[]) => this.bindPracticeTreatmentOptions(data)));

    const bulkEmail =patientSearchTypes.bulkEmailPatientSearch;
    if (!this.isReadOnlyFilter) {
      if (this.pageIdentifier === bulkEmail) {
        this.subscriptions$.add(this.bulkEmailPatientSearchFilter$.subscribe(async (data: PatientSearchFilterModel) => this.bindPatientSearchFilterInformation(data)))
        this.subscriptions$.add(this.bulkStateEvent$.subscribe(async (data: PatientSearchStateEvent) => this.handlePatientSearchStateEvent(data)));
      }
      else {
        this.subscriptions$.add(this.patientSearchFilter$.subscribe(async (data: PatientSearchFilterModel) => this.bindPatientSearchFilterInformation(data)))
        this.subscriptions$.add(this.isLoading$.subscribe(async (data: PatientSearchStateEvent) => this.handlePatientSearchStateEvent(data)));
      }
    }
  }

  bindTreatmentTypes(treatmentTypes: TreatmentType[]) {
    if (treatmentTypes && treatmentTypes.length > 0) {
      this.treatmentTypes = [...treatmentTypes]
        .sort((a, b) => a.practiceTreatmentTypeName.localeCompare(b.practiceTreatmentTypeName))
    }
  }

  bindPracticeTreatmentOptions(data: TreatmentOption[]) {
    this.practiceTreatmentOptions = [...data]
      .sort((a, b) => a.practiceTreatmentOptionName.localeCompare(b.practiceTreatmentOptionName))
  }

  async bindPatientSearchFilterInformation(data: PatientSearchFilterModel) {
    if (data) {
      this.patientSearchFilter.patchValue(data);
    }
  }

  async handlePatientSearchStateEvent(data: PatientSearchStateEvent) {
    this.isLoading = data.loading;
    this.selectAllLoading= data.selectAllLoading??false;
    if (data.showErrorAlert) {
      await this.alert.failureAlert(data.errorMessage);
      this.store.dispatch(closeErrorAlert({pageIdentifier: this.pageIdentifier}));
    }
  }

  onPatientEmailChange(patientSearchFilter: FormGroup): void {
    this.getPatients();
  }

  onPhoneNumberChange(patientSearchFilter: FormGroup): void {
    this.getPatients();
  }

  private sortPracticePtStatus(practicePtStatus: PracticePatientStatus[]) {
    if (!practicePtStatus) {
      return practicePtStatus;
    }
    return practicePtStatus.slice().sort((a, b) => {
      const aValue = a.patientStatusName || '';
      const bValue = b.patientStatusName || '';
      return aValue.localeCompare(bValue);
    });
  }

  private sortPracticeLocation(practiceLocation: PracticeLocation[]) {
    if (!practiceLocation) {
      return practiceLocation;
    }
    return practiceLocation.slice().sort((a, b) => {
      const aValue = a.practiceLocationName || '';
      const bValue = b.practiceLocationName || '';
      return aValue.localeCompare(bValue);
    });
  }

  private sortPracticeTreatmentTypes(treatmentTypes: TreatmentType[]) {
    if (!treatmentTypes) {
      return treatmentTypes;
    }
    return treatmentTypes.slice().sort((a, b) => {
      const aValue = a.practiceTreatmentTypeName || '';
      const bValue = b.practiceTreatmentTypeName || '';
      return aValue.localeCompare(bValue);
    });
  }

  private sortPracticeTreatmentOptions(treatmentOptions: TreatmentOption[]) {
    if (!treatmentOptions) {
      return treatmentOptions;
    }
    return treatmentOptions.slice().sort((a, b) => {
      const aValue = a.practiceTreatmentOptionName || '';
      const bValue = b.practiceTreatmentOptionName || '';
      return aValue.localeCompare(bValue);
    });
  }

  selectPracticeLocation(event: PracticeLocation[], patientSearchFilter: FormGroup): void {
    patientSearchFilter.value.practiceLocationId = event.map(item => item.practiceLocationId).join(',');
    if(!this.isReadOnlyFilter)
      this.dispatchPatientSearchResultAPI(patientSearchFilter.value);
  }

  selectPracticePatientStatus(event: PracticePatientStatus[], patientSearchFilter: FormGroup): void {
    patientSearchFilter.value.patientStatusId = event.map(item => item.patientStatusId).join(',');
    if(!this.isReadOnlyFilter)
     this.dispatchPatientSearchResultAPI(patientSearchFilter.value);
  }

  selectTreatmentType(event: TreatmentType[], patientSearchFilter: FormGroup): void {
    patientSearchFilter.value.patientTreatmentTypes = event.map(item => item.practiceTreatmentTypeId).join(',');
    if(!this.isReadOnlyFilter)
     this.dispatchPatientSearchResultAPI(patientSearchFilter.value);
  }

  selectTreatmentOption(event: TreatmentOption[], patientSearchFilter: FormGroup): void {
    patientSearchFilter.value.treatmentOption = event.map(item => item.practiceTreatmentOptionId).join(',');
    if(!this.isReadOnlyFilter)
     this.dispatchPatientSearchResultAPI(patientSearchFilter.value);
  }

  handleBulkFilterChange(filter: {filterValue:number}): void {
    this.clearBulkEmailPatientSearchFilter();
    if (filter) {
      if (filter.filterValue === 1) {
        this.showAppointmentDateRange = true;
        this.showTreatmentType = false;
        this.showTreatmentOption = false;
        this.patientSearchFilter.get('fromDate')?.setValidators([Validators.required]);
        this.patientSearchFilter.get('toDate')?.setValidators([Validators.required]);
      }
      else if (filter.filterValue === 2) {
        this.showPastdueDaysRange = true;
        this.showTreatmentType = false;
        this.showTreatmentOption = false;
        this.patientSearchFilter.get('fromDays')?.setValidators([Validators.required]);
        this.patientSearchFilter.get('toDays')?.setValidators([Validators.required]);
      } 
      else if (filter.filterValue === 5 || filter.filterValue === 9) {
        this.showTreatmentType = true;
        this.showTreatmentOption = true;
      }
      else {
        this.showTreatmentType = false;
        this.showTreatmentOption = false;
      }
    }
    this.patientSearchFilter.get('fromDays')?.updateValueAndValidity();
    this.patientSearchFilter.get('toDays')?.updateValueAndValidity();
    this.patientSearchFilter.get('fromDate')?.updateValueAndValidity();
    this.patientSearchFilter.get('toDate')?.updateValueAndValidity();
  }

  onFRPNameChange(patientSearchFilter: FormGroup): void {
    const frpFullName = this.commonUtilsService.splitFullName(patientSearchFilter.value.patientFinancialResponsiblePerson)
    patientSearchFilter.value.patientFinancialResponsiblePersonLastName = this.commonUtilsService.trimAndLowerCase(frpFullName.lastName);
    patientSearchFilter.value.patientFinancialResponsiblePersonFirstName = this.commonUtilsService.trimAndLowerCase(frpFullName.firstName);
    this.getPatients();
  }

  onPatientPreferredNameChange(patientSearchFilter: FormGroup): void {
    this.getPatients();
  }

  onPatientDOBChange(patientSearchFilter: FormGroup): void {
    if (this.patientSearchFilter.valid) {
      this.getPatients();
    }
  }

  clearFilter(patientSearchFilter: FormGroup) {
    this.patientSearchFilter.reset();
    this.patientSearchFilter.patchValue({
      showInActivePatients: false
    });
    this.clearBulkEmailPatientSearchFilter();

    if (this.isAutoSearch === true)
      this.dispatchPatientSearchResultAPI(patientSearchFilter.value);
  }

  isValidField(field: string) {
    return this.commonUtilsService.isValidField(field, this.patientSearchFilter)
  }

  getPatients() {
    if(this.isReadOnlyFilter) return;
    if (this.isAutoSearch === true) {
      this.debounceService.debounce('getPatients', () => {
        this.dispatchPatientSearchResultAPI(this.patientSearchFilter.value);
      });
    }
  }

  async searchPatients() {
    if(this.isReadOnlyFilter) return;

    if(this.selectedPatientsBasicInfo.length > 0){
      const response = await this.alert.yesNoWarningAlert(patientSearchMessage.newSearchConfimation)
      if (!response.isConfirmed) {
        return;
      }
    } 
    this.selectedPatientsBasicInfo = [];
    this.formSearchSubmitted = true;
    this.dispatchPatientSearchResultAPI(this.patientSearchFilter.value);
    this.formSearchSubmitted = false;
  }

  dispatchPatientSearchResultAPI(patientSearchFilterValue: PatientSearchFilterModel) {
    if (this.isAutoSearch === true || this.formSearchSubmitted === true) {
      if (this.patientSearchFilter.valid) {
        patientSearchFilterValue = { ...patientSearchFilterValue, 
          patientId: null,
          formSubmitted: true,
          patientPrimaryEmailAddress: patientSearchFilterValue.patientPrimaryEmailAddress?.trim(),
         }
        if (this.pageIdentifier === this.patientSearchType.bulkEmailPatientSearch) {
          this.store.dispatch(invokedBulkPatientSearchFilter({ patientSearchFilter: patientSearchFilterValue, pageIdentifier: this.pageIdentifier }));
        }
        else {
          this.store.dispatch(invokedPatientSearchFilter({ patientSearchFilter: patientSearchFilterValue, pageIdentifier: this.pageIdentifier }));
        }
      }
    }
  }

  onPatientNameChange(patientSearchFilter: FormGroup): void {
    const patientName = this.commonUtilsService.splitFullName(patientSearchFilter.value.patientName);
    patientSearchFilter.value.lastName = this.commonUtilsService.trimAndLowerCase(patientName.lastName);
    patientSearchFilter.value.firstName = this.commonUtilsService.trimAndLowerCase(patientName.firstName);
    this.getPatients();
  }

  closeOverlay() {
    this.closeSearchResultOverlay.emit(true);
  }

  toggleShowActivePatient(event: boolean) {
    this.patientSearchFilter.patchValue({
      showInActivePatients: event
    });
    this.dispatchPatientSearchResultAPI(this.patientSearchFilter.value);
  }

  submitSelectedPatients() {
    if(this.selectedPatientsBasicInfo.length === 0 || this.selectAllLoading || this.isReadOnlyFilter) return;
   
    this.store.dispatch(bulkSelectedPatients({ patients: this.selectedPatientsBasicInfo }));
    this.submitPatients.emit();
  }

  handleSelectedPatients(patients: any) {
    this.selectedPatientsBasicInfo = patients;
  }

  setFilterDatesByDateRange(dateRange: DateRange | null) {
    if (dateRange) {
      this.patientSearchFilter.patchValue({
        fromDate: dateRange.startDate,
        toDate: dateRange.endDate
      });
    }
    else {
      this.patientSearchFilter.patchValue({ fromDate: null, toDate: null });
    }
  }

  clearBulkEmailPatientSearchFilter() {
    this.clearDateRangeForm();
    this.showAppointmentDateRange=false;
    this.showPastdueDaysRange=false;
    this.clearFormValidation();
  }

  @ViewChild('dateRangeComponent') dateRangeComponent!: FormControlDateRangeComponent;
  clearDateRangeForm(): void {
   if(this.dateRangeComponent) this.dateRangeComponent.clearDateRange();
  }

  clearFormValidation() {
    this.patientSearchFilter.get('fromDays')?.clearValidators();
    this.patientSearchFilter.get('fromDays')?.updateValueAndValidity();
    this.patientSearchFilter.get('toDays')?.clearValidators();
    this.patientSearchFilter.get('toDays')?.updateValueAndValidity();
    this.patientSearchFilter.get('fromDate')?.clearValidators();
    this.patientSearchFilter.get('fromDate')?.updateValueAndValidity();
    this.patientSearchFilter.get('toDate')?.clearValidators();
    this.patientSearchFilter.get('toDate')?.updateValueAndValidity();
    this.resetFormSubmittedState();
  }

  resetFormSubmittedState() {
    this.patientSearchFilter.markAsPristine();
    this.patientSearchFilter.markAsUntouched();
    this.patientSearchFilter.updateValueAndValidity();
  }
 
  getSearchFilterDropdownValue() {
    const selectedPracticeLocations =  (this.patientSearchFilter.value.selectedPracticeLocations ?? []).map((item:PracticeLocation) => item.practiceLocationId).join(',');
    const selectedPatientStatusId =  (this.patientSearchFilter.value.selectedPatientStatusId ?? []).map((item:PracticePatientStatus) => item.patientStatusId).join(',');
    const patientTreatmentTypes =  (this.patientSearchFilter.value.patientTreatmentTypeIds ?? []).map((item:TreatmentType) => item.practiceTreatmentTypeId).join(',');
    const treatmentOption =  (this.patientSearchFilter.value.practiceTreatmentOptionIds ?? [] ).map((item:TreatmentOption) => item.practiceTreatmentOptionId).join(',');
    
    this.patientSearchFilter.patchValue({
      practiceLocationId: selectedPracticeLocations,
      patientStatusId: selectedPatientStatusId,
      patientTreatmentTypes: patientTreatmentTypes,
      treatmentOption: treatmentOption
    });
  }
  ngOnDestroy() {
    this.subscriptions$.unsubscribe();
    if(this.pageIdentifier===this.patientSearchType.bulkEmailPatientSearch)
      this.store.dispatch(bulkPatientSearchStateDestroy());
  }
}